import { useState, useEffect } from "react";
import { CustomSelect } from "@/components/CustomSelect";
import { DateInput } from "@/components/DateInput";
import { fmtDate } from "@/lib/dates";
import { Plus, Pencil, Trash2, MapPin, Calendar, X, Images, List, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr, fileUrl, uploadFile } from "@/lib/api";
import { Button } from "@/components/Button";
import { Modal, Field, TextInput, TextArea } from "@/components/Modal";
import { EmptyState, Loader, Portal } from "@/components/common";

const CATS = ["Got Together", "First Date", 'First "I Love You"', "Got Engaged", "Got Married",
  "Found Out We Were Pregnant", "Baby Born", "Moved In Together", "First Trip", "Favorite Memory", "Custom"];

export function MemoriesSection({ cid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sort, setSort] = useState("newest");
  const [lightbox, setLightbox] = useState(null);
  const [view, setView] = useState("timeline");
  const [galleryYear, setGalleryYear] = useState("All");
  const [galleryTag, setGalleryTag] = useState("All");
  const [gIndex, setGIndex] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/connections/${cid}/memories`, { params: { sort } });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid, sort]);

  const startAdd = () => { setEditing(null); setForm({ title: "", category: "Favorite Memory", date: "", time: "", location: "", story: "", photos: [], tags: [] }); setOpen(true); };
  const startEdit = (m) => { setEditing(m); setForm({ ...m, title: m.title === m.category ? "" : m.title, tags: m.tags || [], photos: m.photos || [] }); setOpen(true); };

  const addPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = await uploadFile(file);
      setForm((f) => ({ ...f, photos: [...(f.photos || []), path] }));
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!form.category?.trim() || form.category === "Custom") return toast.error("Choose a category (or type your custom one).");
    if (!form.date) return toast.error("Pick a date for this memory.");
    setBusy(true);
    try {
      const payload = { title: form.title, category: form.category, date: form.date || null, time: form.time || null, location: form.location || null, story: form.story || "", photos: form.photos || [], tags: (form.tags || []) };
      if (editing) await api.put(`/connections/${cid}/memories/${editing.id}`, payload);
      else await api.post(`/connections/${cid}/memories`, payload);
      setOpen(false); await load(); toast.success("Memory saved");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const del = async (m) => { if (confirm("Delete this memory?")) { await api.delete(`/connections/${cid}/memories/${m.id}`); await load(); } };

  if (loading) return <Loader />;

  // Flatten all photos across memories for gallery view
  const allPhotos = [];
  items.forEach((m) => {
    const year = (m.date || "").slice(0, 4) || "Undated";
    (m.photos || []).forEach((p) => allPhotos.push({ url: p, memory: m, year }));
  });
  const years = ["All", ...Array.from(new Set(allPhotos.map((p) => p.year))).sort().reverse()];
  const allTags = ["All", ...Array.from(new Set(items.flatMap((m) => m.tags || [])))];
  const galleryPhotos = allPhotos.filter(
    (p) => (galleryYear === "All" || p.year === galleryYear) &&
           (galleryTag === "All" || (p.memory.tags || []).includes(galleryTag))
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
        <div className="flex gap-1 p-1 bg-slate-900/60 rounded-xl">
          <button onClick={() => setView("timeline")} data-testid="view-timeline" className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${view === "timeline" ? "bg-blue-600 text-white" : "text-slate-400"}`}><List size={15} /> <span className="hidden min-[400px]:inline">Timeline</span></button>
          <button onClick={() => setView("gallery")} data-testid="view-gallery" className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${view === "gallery" ? "bg-blue-600 text-white" : "text-slate-400"}`}><Images size={15} /> <span className="hidden min-[400px]:inline">Gallery</span></button>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {view === "timeline" && (
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="iks-input px-3 py-2 text-sm">
              <option value="newest" className="bg-slate-900">Newest</option>
              <option value="oldest" className="bg-slate-900">Oldest</option>
            </select>
          )}
          <Button onClick={startAdd} data-testid="add-memory-btn"><Plus size={16} /> Add</Button>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={Calendar} title="No memories yet" subtitle="Save your first memory together." action={<Button onClick={startAdd}><Plus size={16} /> Add Memory</Button>} testId="memories-empty" />
      ) : view === "gallery" ? (
        <div data-testid="memory-gallery">
          <div className="flex flex-wrap gap-1.5 mb-4">
            {years.map((y) => (
              <button key={y} onClick={() => setGalleryYear(y)} className={`px-3 py-1 rounded-full text-xs font-medium ${galleryYear === y ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400"}`}>{y}</button>
            ))}
            {allTags.length > 1 && <span className="w-px bg-white/10 mx-1" />}
            {allTags.map((t) => (
              <button key={t} onClick={() => setGalleryTag(t)} className={`px-3 py-1 rounded-full text-xs font-medium ${galleryTag === t ? "bg-sky-500 text-white" : "bg-slate-800 text-slate-400"}`}>{t === "All" ? "All tags" : `#${t}`}</button>
            ))}
          </div>
          {galleryPhotos.length === 0 ? (
            <EmptyState icon={Images} title="No photos here" subtitle="Add photos to your memories to see them in the gallery." />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
              {galleryPhotos.map((p, i) => (
                <button key={i} onClick={() => setGIndex(i)} data-testid="gallery-photo" className="relative aspect-square overflow-hidden rounded-lg group">
                  <img src={fileUrl(p.url)} alt="" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 text-[10px] text-white/90 text-left truncate">{p.memory.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-5">
          {items.map((m) => (
            <div key={m.id} className="iks-card overflow-hidden group" data-testid="memory-card">
              {m.photos?.length > 0 && (
                <div className={`grid gap-1 ${m.photos.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                  {m.photos.slice(0, 4).map((p, i) => (
                    <img key={i} src={fileUrl(p)} alt="" onClick={() => setLightbox(fileUrl(p))}
                      className={`w-full object-cover cursor-pointer ${m.photos.length === 1 ? "max-h-96" : "h-40"}`} />
                  ))}
                </div>
              )}
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-400">{m.category}</span>
                    <h3 className="font-display font-semibold text-lg text-slate-50 mt-0.5">{m.title}</h3>
                  </div>
                  <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => startEdit(m)} className="p-1.5 text-slate-400 hover:text-sky-400"><Pencil size={15} /></button>
                    <button onClick={() => del(m)} className="p-1.5 text-slate-400 hover:text-red-400"><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 mt-1.5">
                  {m.date && <span className="flex items-center gap-1"><Calendar size={12} /> {fmtDate(m.date)}{m.time ? ` · ${m.time}` : ""}</span>}
                  {m.location && <span className="flex items-center gap-1"><MapPin size={12} /> {m.location}</span>}
                </div>
                {m.story && <p className="text-slate-300 text-sm mt-3 leading-relaxed whitespace-pre-wrap">{m.story}</p>}
                {m.tags?.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap mt-3">
                    {m.tags.map((t) => <span key={t} className="text-xs text-sky-300 bg-sky-500/10 px-2 py-0.5 rounded-full">#{t}</span>)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <Portal>
        <div className="fixed inset-0 z-[1000] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button className="absolute right-4 text-white/80" style={{ top: "calc(1rem + env(safe-area-inset-top))" }}><X size={28} /></button>
          <img src={lightbox} alt="" className="max-w-full max-h-full rounded-xl" />
        </div>
        </Portal>
      )}

      {gIndex != null && galleryPhotos[gIndex] && (
        <Portal>
        <div className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center" data-testid="gallery-lightbox">
          <button onClick={() => setGIndex(null)} className="absolute right-4 text-white/80 z-10" style={{ top: "calc(1rem + env(safe-area-inset-top))" }}><X size={28} /></button>
          {gIndex > 0 && <button onClick={() => setGIndex(gIndex - 1)} className="absolute left-3 text-white/70 hover:text-white p-2" data-testid="gallery-prev"><ChevronLeft size={32} /></button>}
          {gIndex < galleryPhotos.length - 1 && <button onClick={() => setGIndex(gIndex + 1)} className="absolute right-3 text-white/70 hover:text-white p-2" data-testid="gallery-next"><ChevronRight size={32} /></button>}
          <div className="max-w-4xl max-h-[85vh] flex flex-col items-center px-12">
            <img src={fileUrl(galleryPhotos[gIndex].url)} alt="" className="max-w-full max-h-[75vh] rounded-xl object-contain" />
            <div className="mt-3 text-center">
              <p className="text-white font-medium">{galleryPhotos[gIndex].memory.title}</p>
              <p className="text-slate-400 text-sm">{galleryPhotos[gIndex].memory.date || "Undated"} · {gIndex + 1} / {galleryPhotos.length}</p>
            </div>
          </div>
        </div>
        </Portal>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit memory" : "Add memory"} testId="memory-modal"
        footer={<><Button variant="secondary" full onClick={() => setOpen(false)}>Cancel</Button><Button full loading={busy} onClick={save} data-testid="save-memory-btn">Save</Button></>}>
        <Field label="Title (optional)" hint="Leave blank to use the category as the title"><TextInput value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder={form.category && form.category !== "Custom" ? form.category : "Our first trip"} data-testid="memory-title" /></Field>
        <Field label="Category">
          <CustomSelect options={CATS} value={form.category} onChange={(v) => setForm((f) => ({ ...f, category: v }))} placeholder="e.g. First Concert Together" testId="memory-category" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <Field label="Date"><DateInput value={form.date || ""} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="memory-date" /></Field>
          <Field label="Time (optional)"><TextInput type="time" value={form.time || ""} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} /></Field>
        </div>
        <Field label="Location (optional)"><TextInput value={form.location || ""} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="Where did it happen?" /></Field>
        <Field label="Story / notes"><TextArea value={form.story} onChange={(e) => setForm((f) => ({ ...f, story: e.target.value }))} placeholder="Tell the story…" /></Field>
        <Field label="Tags (comma separated)"><TextInput value={(form.tags || []).join(", ")} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }))} placeholder="vacation, 2026" /></Field>
        <Field label="Photos">
          <label className="iks-input w-full px-3.5 py-3 text-sm flex items-center justify-center gap-2 text-slate-300 hover:border-sky-400/50 cursor-pointer">
            <Plus size={16} /> {uploading ? "Uploading…" : "Add photo"}
            <input type="file" accept="image/*" hidden onChange={addPhoto} />
          </label>
          {form.photos?.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mt-3">
              {form.photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={fileUrl(p)} alt="" className="w-full h-20 object-cover rounded-lg" />
                  <button onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))} className="absolute -top-1.5 -right-1.5 bg-red-500 rounded-full p-0.5"><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </Field>
      </Modal>
    </div>
  );
}
